import { TourProvider as ReactTourProvider } from '@reactour/tour';
import { PropsWithChildren } from 'preact/compat';

import { trackMP } from '@/shared/mp';

import TourStepPopover from './TourStepPopover';
import useTourTracking from './useTourTracking';

interface TourOnboardingProps extends PropsWithChildren {}

const commonPopoverStyles = {
  popover: (base: any) => ({
    ...base,
    padding: 0,
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
    background: 'transparent',
  }),
};

const getPopoverStyles = (customStyles = {}) => ({
  popover: (base: any) => ({
    ...commonPopoverStyles.popover(base),
    ...customStyles,
  }),
});

const TourOnboarding = ({ children }: TourOnboardingProps) => {
  const { trackStarted, trackSkip, trackCompletion } = useTourTracking();

  const tourSteps = [
    {
      // this is invisible target and since the start-exploring doesnt exist it masks the whole bg as we wish
      selector: '[data-tour="start-exploring"]',
      position: [window?.innerWidth * 0.1 || 100, window?.innerHeight * 0.1 || 100] as [number, number],
      padding: { mask: 0 },
      styles: commonPopoverStyles,
      content: ({ setCurrentStep, setIsOpen }: any) => (
        <TourStepPopover
          title="👋 New here?"
          description="Let us show you around!"
          onRender={trackStarted}
          primaryButton={{
            text: 'Take A Tour',
            onClick: () => setCurrentStep(1),
          }}
          secondaryButton={{
            text: 'Skip',
            onClick: () => {
              trackSkip();
              setIsOpen(false);
              trackMP('educators-onboarding-skipped');
            },
          }}
        />
      ),
    },
    {
      selector: '[data-tour="live-streams-sidebar"]',
      padding: { mask: 0 },
      styles: commonPopoverStyles,
      content: ({ setCurrentStep }: any) => (
        <TourStepPopover
          title="Live Animal Streams!"
          description="Select or search for animal live streams"
          secondaryButton={{
            text: 'Next',
            onClick: () => setCurrentStep(2),
          }}
        />
      ),
    },
    {
      selector: '[data-tour="live-events-button"]',
      padding: { mask: 15 },
      styles: commonPopoverStyles,
      content: ({ setCurrentStep }: any) => (
        <TourStepPopover
          title="LIVE Events"
          description="Attend expert talks, vet check-ups, or book your private Q&A session with an animal expert."
          secondaryButton={{
            text: 'Next',
            onClick: () => setCurrentStep(3),
          }}
        />
      ),
    },
    {
      selector: '[data-tour="educator-resources"]',
      padding: { mask: 15 },
      styles: commonPopoverStyles,
      content: ({ setCurrentStep }: any) => (
        <TourStepPopover
          title="Educator Resources"
          description="Select from a library of lesson plans and activities to engage students in class & beyond"
          secondaryButton={{
            text: 'Next',
            onClick: () => setCurrentStep(4),
          }}
        />
      ),
    },
    {
      selector: '[data-tour="home-video-section"]',
      padding: { mask: 0 },
      styles: getPopoverStyles({ marginLeft: '200px' }),
      content: ({ setIsOpen }: any) => (
        <TourStepPopover
          title="Your Home for Featured Highlights"
          description="Watch rotating live streams on the homepage, or browse the video library below"
          secondaryButton={{
            text: 'Start Exploring!',
            onClick: () => {
              trackCompletion();
              setIsOpen(false);
              trackMP('educators-onboarding-completed');
            },
          }}
        />
      ),
    },
  ];

  return (
    <ReactTourProvider
      steps={tourSteps}
      showNavigation={false}
      showCloseButton={false}
      showBadge={false}
      onClickMask={() => {}}
      disableInteraction
    >
      {children}
    </ReactTourProvider>
  );
};

export default TourOnboarding;
